/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PageContainer from 'components/PageContainer/PageContainer';
import PasosDeCompra from 'components/public/FichaEspectaculo/PasosDeCompra';
import { Backdrop, Box, Button, CircularProgress, Typography } from '@mui/material';
import { theme } from 'theme/theme';
import { getAuthToken } from 'services/apiUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, LocalActivityOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Funcion } from 'utils/models/types';
import { aprobarPagoTest, pagoEntrada } from 'services/servicioPost';
import { useError } from 'services/ErrorContext';
import Countdown from '../../public/Countdown/Countdown';
import { formatoFechaHora, getCompraConExpiracion } from 'utils/utilities';
import { anchoMaxForms } from 'utils/constants';
import NotAllowed from 'pages/auxiliares/NotAllowed';

const PAYMENT_FLOW_MODE_KEY = 'paymentFlowMode';

const CompraEntrada = () => {
  const { id } = useParams();

  useEffect(() => {
    if (id) localStorage.setItem('idCompra', id);
  }, [id]);

  const datosCompraJSON = getCompraConExpiracion('datosCompra');
  const datosCompra = datosCompraJSON && JSON.parse(datosCompraJSON);
  const [openSpinner, setOpenSpinner] = useState(false);
  const navigate = useNavigate();
  const token = getAuthToken();
  const { setError, setLoading } = useError();

  const precioAPagar = datosCompra?.responseCompra.SubTotal * (1 - datosCompra?.responseCompra?.Descuento / 100);
  const expiracionJSON = localStorage.getItem('expiracionCompra');
  const expiracion = expiracionJSON && JSON.parse(expiracionJSON);

  const currentTime = new Date().getTime();
  const remainingTime = expiracion - currentTime;

  useEffect(() => {
    if (expiracion && remainingTime <= 0) {
      localStorage.removeItem('datosCompra');
      localStorage.removeItem('expiracionCompra');
      setTimeout(() => {
        navigate('/');
      }, 5000);
    }
  }, [remainingTime, navigate]);

  const funcion: Funcion = datosCompra?.Funciones?.find((f: Funcion) => f.Id === datosCompra?.FuncionId);
  const fecha = new Date(funcion?.Fecha.FechaInicio);
  const fechaHora = datosCompra && formatoFechaHora(fecha);

  if (!datosCompra) {
    return <NotAllowed />;
  }

  const handleConfirmar = async () => {
    setOpenSpinner(true);

    const id = localStorage.getItem('idCompra');

    if (token) {
      if (datosCompra?.responseCompra?.Descuento === 100) {
        // Para descuento del 100%, navegar directamente a success
        navigate('/payment/success');
        return;
      }

      try {
        const response = await pagoEntrada({ IdCompra: Number(id) }, setError, setLoading);

        if (response && response.InitPoint) {
          if (response.InitPoint === 'TEST_MODE') {
            if (!response.CompraId) {
              console.error('TEST_MODE sin CompraId. Respuesta:', response);
              setError('No se pudo iniciar el pago en modo testing');
              return;
            }

            localStorage.setItem(PAYMENT_FLOW_MODE_KEY, 'test');
            const testPayment = await aprobarPagoTest(response.CompraId, setError, setLoading);

            if (!testPayment) {
              localStorage.removeItem(PAYMENT_FLOW_MODE_KEY);
              setError('No se pudo aprobar el pago de prueba');
              return;
            }

            navigate('/payment/success');
            return;
          }

          // Redirigir a MercadoPago
          window.location.href = response.InitPoint;
        } else {
          console.error('No se obtuvo InitPoint válido. Respuesta:', response);
          setError('No se pudo iniciar el proceso de pago');
        }
      } catch (error) {
        console.error('Error creando el pago:', error);
        setError('Error al iniciar el proceso de pago');
      } finally {
        setOpenSpinner(false);
      }
    } else {
      navigate('/login');
    }
  };

  const mensajeExpiracion = expiracion
    ? 'Los datos de tu entrada se mantendrán guardados por 5 minutos'
    : 'El tiempo para hacer la compra ha expirado';

  return (
    <PageContainer marginBottom={true}>
      {expiracion && (
        <Countdown
          tiempoExpiracion={expiracion}
          mensaje={mensajeExpiracion}
          severity={expiracion ? 'info' : 'error'}
          titulo=""
        />
      )}
      <Box width={anchoMaxForms} maxWidth={anchoMaxForms} mx="auto">
        <PasosDeCompra paso={1} />
        <Button
          fullWidth={false}
          sx={{ marginTop: '24px', position: 'absolute!important', top: '0px', left: { xs: '0px', sm: '100px' } }}
          onClick={() => navigate(-1)}
          startIcon={<ChevronLeft />}
        >
          Volver
        </Button>
        <>
          <Box
            width={{ xs: 'unset', sm: '480px' }}
            lineHeight={2}
            paddingX="30px"
            paddingY="15px"
            boxSizing="border-box"
            my={2}
            mx="auto"
            sx={{ backgroundColor: theme.palette.secondary.light }}
            borderRadius={4}
          >
            <Typography variant="body1" sx={{ mb: 2 }} color={theme.palette.primary.dark}>
              <b>Resumen de tu compra</b>
            </Typography>

            <Typography variant="body2">{`Has elegido comprar ${datosCompra?.CantAsientos} entrada/s para:`}</Typography>
            <Typography variant="h6">{datosCompra?.Nombre}</Typography>
            <Typography variant="body2">Para el día:</Typography>
            <Typography variant="body2">
              <b>{fechaHora.toUpperCase()}</b>
            </Typography>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Typography variant="body1">
                <b>Sub-total:</b>
              </Typography>
              <Typography variant="body1">${datosCompra?.CantAsientos * datosCompra?.Precio}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Typography variant="body1">
                <b>Descuento:</b>
              </Typography>
              <Typography variant="body1">{datosCompra?.responseCompra?.Descuento} %</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Typography variant="h6">
                <b>Total a pagar:</b>
              </Typography>
              <Typography variant="h6">${precioAPagar.toFixed(2)}</Typography>
            </Box>
            <form
              onSubmit={e => {
                e.preventDefault();
                handleConfirmar();
              }}
            >
              <LoadingButton
                type="submit"
                size="large"
                variant="contained"
                sx={{
                  mt: 4,
                  mb: 2,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
                fullWidth
                loadingPosition="start"
                disabled={remainingTime <= 0}
                startIcon={<LocalActivityOutlined />}
              >
                Confirmar compra
              </LoadingButton>
            </form>
          </Box>
          <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={openSpinner}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </>
      </Box>
    </PageContainer>
  );
};

export default CompraEntrada;
